import type { ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { X } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";

import { OnboardingBackgroundShader } from "./OnboardingBackgroundShader";

const SLIDE_TRANSITION = { duration: 0.32, ease: [0.16, 1, 0.3, 1] as const };

type OnboardingShellProps = {
  contentKey: number | string;
  showProgress: boolean;
  progressTotal: number;
  progressCurrent: number;
  ctaLabel?: string;
  ctaBusy?: boolean;
  onCta?: () => void;
  secondaryCtaLabel?: string;
  onSecondaryCta?: () => void;
  onSkip?: () => void;
  /** When true, show the header close control on native and web (not web-only). */
  showCloseButton?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  /** Paywall: inner slide owns scroll; shell main must not compete. */
  disableContentScroll?: boolean;
  /** Paywall: let fixed footer extend to the physical screen bottom. */
  flushBottom?: boolean;
  /** Paywall: remove horizontal shell padding so slide footer can span edge-to-edge. */
  flushHorizontal?: boolean;
  children: ReactNode;
};

export function OnboardingShell({
  contentKey,
  showProgress,
  progressTotal,
  progressCurrent,
  ctaLabel,
  ctaBusy,
  onCta,
  secondaryCtaLabel,
  onSecondaryCta,
  onSkip,
  showCloseButton = false,
  hideHeader = false,
  hideFooter = false,
  disableContentScroll = false,
  flushBottom = false,
  flushHorizontal = false,
  children,
}: OnboardingShellProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const progressPercent =
    progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;
  const showClose =
    onSkip != null && (showCloseButton || Capacitor.getPlatform() === "web");

  return (
    <div className="fixed inset-0 z-50 flex min-h-[100svh] flex-col text-[oklch(90%_0.02_150)]">
      <OnboardingBackgroundShader />

      <div
        className={`relative z-10 flex min-h-[100svh] flex-1 flex-col pt-[max(18px,env(safe-area-inset-top))] ${
          flushHorizontal
            ? ""
            : "px-[max(18px,env(safe-area-inset-left))] pr-[max(18px,env(safe-area-inset-right))]"
        } ${flushBottom ? "pb-0" : "pb-[max(18px,env(safe-area-inset-bottom))]"}`}
      >
        <header
          className={`mb-4 flex shrink-0 items-center gap-3 ${
            hideHeader ? "justify-end" : "justify-between"
          } ${flushHorizontal ? "px-[max(18px,env(safe-area-inset-left))] pr-[max(18px,env(safe-area-inset-right))]" : ""}`}
        >
          {!hideHeader ? (
            <span className="flex items-center gap-0.1 text-lg font-semibold tracking-tight">
              <img
                src="/brand/screen0-app-icon.png"
                alt=""
                width={24}
                height={24}
                className="h-6 w-6 shrink-0 rounded-[6px]"
                decoding="async"
              />
              {t.common.appName}
            </span>
          ) : null}

          <div
            className={`flex min-w-0 items-center justify-end gap-3 ${hideHeader ? "" : "flex-1"}`}
          >
            {showProgress ? (
              <div
                className="h-1.5 w-full max-w-[140px] overflow-hidden rounded-full bg-[oklch(22%_0.03_160)]"
                role="progressbar"
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t.onboarding.progressLabel}
              >
                <div
                  className="h-full rounded-full bg-[oklch(72%_0.09_155)] transition-[width] duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            ) : null}

            {showClose ? (
              <button
                type="button"
                className="icon-button shrink-0"
                aria-label={t.common.close}
                onClick={onSkip}
              >
                <X size={20} />
              </button>
            ) : null}
          </div>
        </header>

        <main className="relative min-h-0 flex-1 overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.div
              key={contentKey}
              className={`absolute inset-0 flex flex-col ${
                disableContentScroll
                  ? "overflow-hidden"
                  : "overflow-y-auto overscroll-y-contain"
              }`}
              initial={
                prefersReducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0.85, x: "100%" }
              }
              animate={{ opacity: 1, x: 0 }}
              exit={
                prefersReducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0.85, x: "-100%" }
              }
              transition={
                prefersReducedMotion
                  ? { duration: 0.15 }
                  : SLIDE_TRANSITION
              }
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {ctaLabel && onCta && !hideFooter ? (
          <footer className="mt-4 flex shrink-0 flex-col items-center gap-3 pt-2">
            {secondaryCtaLabel && onSecondaryCta ? (
              <button
                type="button"
                className="min-h-[44px] px-2 text-xs font-bold uppercase tracking-[0.12em] text-[oklch(72%_0.09_155)] transition-opacity hover:opacity-80 active:opacity-70"
                onClick={onSecondaryCta}
              >
                {secondaryCtaLabel}
              </button>
            ) : null}
            <button
              type="button"
              className="primary-action w-full disabled:opacity-60"
              disabled={ctaBusy}
              onClick={onCta}
            >
              {ctaBusy ? t.onboarding.working : ctaLabel}
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
